import { EyeOutlined } from '@ant-design/icons';
import { Modal } from 'antd/lib';
import Button from 'antd/lib/button';
import Empty from 'antd/lib/empty';
import Row from 'antd/lib/row';
import Spin from 'antd/lib/spin';
import Timeline from 'antd/lib/timeline';
import Typography from 'antd/lib/typography';
import React, { useMemo, useState } from 'react';
import { defaultDateTimeFormat } from '../../domain/common/dateFormatters';
import { IEmailUtilityAutomation, IEmailUtilityAutomationHistory } from '../../domain/email-utility-automation/interface';
import { useEmailUtilityAutomationHistoryInfiniteListQuery } from '../../domain/email-utility-automation/queries';
import { EmailUtilityAutomationHistoryContentModal } from './EmailUtilityAutomationHistoryContentModal';
import { getEmailUtilityAutomationHistoryStatusColor, getEmailUtilityAutomationHistoryText } from './helpers';

interface IUtilityAutomationHistoryModalProps {
    utilityAutomation: IEmailUtilityAutomation;
    onClose: (needRefresh?: boolean) => void;
}

export const EmailUtilityAutomationHistoryModal: React.FC<IUtilityAutomationHistoryModalProps> = ({ utilityAutomation, onClose }) => {
    const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useEmailUtilityAutomationHistoryInfiniteListQuery(
        utilityAutomation.automation_key
    );
    const [previewMessageId, setPreviewMessageId] = useState<string | null>(null);

    const timeLineItems = useMemo(
        () => (data?.pages || []).flat().map(historyItem => transformToTimelineItem(historyItem, setPreviewMessageId)),
        [data]
    );

    const handleClose = () => {
        onClose();
    };

    const ModalContent = () => {
        if (isLoading) {
            return (
                <Row justify="center" style={{ width: '100%', padding: '8px 0' }}>
                    <Spin size="large" />
                </Row>
            );
        }

        if (isError) {
            return (
                <Row justify="center" style={{ width: '100%', padding: '8px 0' }}>
                    <Typography.Text>An Error occurred!</Typography.Text>
                </Row>
            );
        }

        if (timeLineItems.length === 0) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'No history available'} />;
        }

        return (
            <>
                <Timeline style={{ padding: 16 }} mode="left" items={timeLineItems} />
                {hasNextPage && (
                    <Button block loading={isFetchingNextPage} disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                        Load More
                    </Button>
                )}
            </>
        );
    };

    return (
        <>
            <Modal
                open
                width={500}
                title={`${utilityAutomation.title} parser history for 12 months`}
                destroyOnHidden
                onCancel={handleClose}
                loading={isLoading}
                styles={{ body: { maxHeight: '600px', overflow: 'auto' }, header: { paddingRight: 42 } }}
                footer={[
                    <Button key="cancel" onClick={handleClose}>
                        Close
                    </Button>,
                ]}
            >
                <ModalContent />
            </Modal>
            {previewMessageId && (
                <EmailUtilityAutomationHistoryContentModal messageId={previewMessageId} mask={false} onClose={() => setPreviewMessageId(null)} />
            )}
        </>
    );
};

function transformToTimelineItem(historyItem: IEmailUtilityAutomationHistory, onPreview: (messageId: string) => void) {
    const formattedDateTime = defaultDateTimeFormat(historyItem.datetime);

    return {
        key: historyItem.message_id,
        label: formattedDateTime,
        children: (
            <>
                {getEmailUtilityAutomationHistoryText(historyItem)}
                <br />
                <Button size="small" icon={<EyeOutlined />} onClick={() => onPreview(historyItem.message_id)}>
                    View Email Content
                </Button>
            </>
        ),
        color: getEmailUtilityAutomationHistoryStatusColor(historyItem.status),
    };
}
